import type { Block, Document, Person, Recommendation, Insights, Section, VibelinProject } from './types';

const P = (text: string): Block => ({ t: 'p', text });
const UL = (items: string[]): Block => ({ t: 'ul', items });
const KV = (rows: [string, string][]): Block => ({ t: 'kv', rows });

export const project: VibelinProject = {
  name: 'Helios Retail Platform',
  code: 'HELIOS',
  env: 'Workspace',
};

export const people: Record<string, Person> = {
  ad: { id: 'ad', name: 'Ava Delgado', initials: 'AD', hue: 258, role: 'Product Lead' },
  rk: { id: 'rk', name: 'Ravi Kapoor', initials: 'RK', hue: 300, role: 'Staff Engineer' },
  mn: { id: 'mn', name: 'Mina Novak', initials: 'MN', hue: 150, role: 'Business Analyst' },
  ai: { id: 'ai', name: 'Vibelin AI', initials: 'AI', hue: 300, role: 'Agent', isAI: true },
};

const prdSections: Section[] = [
  {
    id: 's-summary', title: 'Executive Summary', level: 1, status: 'filled', author: 'ad',
    blocks: [
      P('Adaptive Inventory Forecasting replaces Helios\' static reorder points with a continuously-learning model that predicts demand at the SKU–store grain. The system ingests point-of-sale, weather, promotion, and supplier-lead-time signals to recommend purchase quantities 7–28 days ahead.'),
      P('This document defines the scope, requirements, and success criteria for the v1 launch targeting the North America grocery vertical in Q3.'),
    ],
  },
  {
    id: 's-problem', title: 'Problem Statement', level: 1, status: 'filled', author: 'mn', hasFlag: true,
    blocks: [
      P('Helios merchandising teams currently set reorder points manually each quarter. The process is slow, error-prone, and unable to react to short-term demand shifts.'),
      UL([
        'Stock-outs on high-velocity SKUs cost an estimated $14.2M in lost sales annually.',
        'Overstock on slow movers ties up ~9% of working capital in dead inventory.',
        'Planners spend 60% of their week on spreadsheet reconciliation rather than strategy.',
      ]),
    ],
  },
  {
    id: 's-goals', title: 'Goals & Success Metrics', level: 1, status: 'filled', author: 'ad',
    blocks: [],
    children: [
      {
        id: 's-goals-primary', title: 'Primary Goals', level: 2, status: 'filled',
        blocks: [
          UL([
            'Reduce stock-out rate on A-class SKUs from 6.8% to under 2%.',
            'Cut excess inventory holding cost by 20% within two quarters.',
            'Give planners a single recommended order quantity they can accept in one click.',
          ]),
        ],
      },
      {
        id: 's-goals-kpis', title: 'Success Metrics', level: 2, status: 'filled',
        blocks: [
          KV([
            ['Forecast accuracy (WAPE)', '≤ 18% at 14-day horizon'],
            ['Stock-out rate, A-class', '< 2.0%'],
            ['Planner acceptance rate', '> 70% of recommendations'],
            ['Time-to-decision', '< 45s per replenishment'],
          ]),
        ],
      },
    ],
  },
  {
    id: 's-personas', title: 'User Personas', level: 1, status: 'empty',
    prompt: 'Generate 2–3 user personas for an inventory forecasting tool used by retail merchandising teams. Include goals, frustrations, and how this product helps each.',
  },
  {
    id: 's-requirements', title: 'Requirements', level: 1, status: 'filled', author: 'rk',
    blocks: [],
    children: [
      {
        id: 's-req-func', title: 'Functional Requirements', level: 2, status: 'filled',
        blocks: [
          UL([
            'FR-1 — Generate a daily replenishment recommendation per SKU–store with confidence interval.',
            'FR-2 — Allow planners to accept, adjust, or reject any recommendation with an audit trail.',
            'FR-3 — Surface the top three demand drivers behind each forecast.',
            'FR-4 — Support manual demand overrides for known events (e.g. local festivals).',
          ]),
        ],
      },
      {
        id: 's-req-nonfunc', title: 'Non-Functional Requirements', level: 2, status: 'empty',
        prompt: 'Generate non-functional requirements (performance, scalability, availability, security, compliance) for a SKU-level demand forecasting service serving 1,800 stores.',
      },
    ],
  },
  {
    id: 's-scope', title: 'Out of Scope', level: 1, status: 'empty',
    prompt: 'List what is explicitly out of scope for v1 of an inventory forecasting product, with a one-line rationale for each.',
  },
  {
    id: 's-questions', title: 'Open Questions', level: 1, status: 'filled', author: 'mn',
    blocks: [
      UL([
        'Do we forecast at SKU–store or SKU–region grain for low-velocity items?',
        'How do we handle the cold-start problem for newly introduced SKUs?',
        'Who owns the override approval workflow — planner or category manager?',
      ]),
    ],
  },
];

export const documents: Document[] = [
  { id: 'doc-market', code: 'MR-014', type: 'Market Research', category: 'Discovery', title: 'Grocery Demand Volatility 2026', owner: 'mn', updated: '3 days ago', progress: 100, sections: 9, status: 'approved' },
  { id: 'doc-case', code: 'BC-007', type: 'Business Case', category: 'Discovery', title: 'Forecasting Platform Investment', owner: 'ad', updated: 'yesterday', progress: 100, sections: 6, status: 'approved' },
  { id: 'doc-prd', code: 'PRD-031', type: 'Product Requirements', category: 'Definition', title: 'Adaptive Inventory Forecasting', owner: 'ad', updated: '12 min ago', progress: 64, sections: 7, status: 'in-review', active: true, sectionsData: prdSections },
  { id: 'doc-fsd', code: 'FSD-022', type: 'Functional Spec', category: 'Definition', title: 'Forecast Service & Planner UX', owner: 'rk', updated: '1 hour ago', progress: 42, sections: 11, status: 'draft' },
  { id: 'doc-adr', code: 'ADR-009', type: 'Architecture Decision', category: 'Architecture', title: 'Feature Store & Model Serving', owner: 'rk', updated: '2 days ago', progress: 80, sections: 5, status: 'draft' },
];

export const recommendations: Recommendation[] = [
  { id: 'r1', kind: 'improve', section: 's-problem', title: 'Quantify the planner time cost', body: 'The Problem Statement cites 60% of planner time but no headcount or dollar figure. Add a derived cost to strengthen the business case linkage.', action: 'Apply suggestion' },
  { id: 'r2', kind: 'conflict', section: 's-goals-kpis', title: 'Metric conflicts with FSD-022 §3.2', body: 'This PRD targets WAPE ≤ 18% at 14 days, but the Functional Spec assumes 21%. Align before review.', action: 'Open FSD-022' },
  { id: 'r3', kind: 'gap', section: 's-personas', title: 'Missing section: User Personas', body: 'PRDs in this project average 3 personas. This section is empty — generate a first draft from the problem statement.', action: 'Generate section' },
  { id: 'r4', kind: 'visualize', section: 's-goals', title: 'Visualize the metric tree', body: 'Goals and success metrics map cleanly to a driver tree. Generate a diagram to include in the review deck.', action: 'Generate diagram' },
];

export const insights: Insights = {
  relations: [
    { from: 'PRD-031 · Goals', to: 'BC-007 · ROI Model', strength: 'strong', note: 'Success metrics feed the investment payback calc.' },
    { from: 'PRD-031 · Requirements', to: 'FSD-022 · API Contract', strength: 'strong', note: '12 functional requirements traced to spec.' },
    { from: 'PRD-031 · Problem', to: 'MR-014 · Findings', strength: 'medium', note: 'Stock-out cost sourced from market research.' },
    { from: 'PRD-031 · Goals', to: 'ADR-009 · Latency', strength: 'medium', note: '45s decision target constrains serving design.' },
  ],
  impact: {
    title: 'If you change "WAPE ≤ 18%"',
    affected: [
      { doc: 'FSD-022', detail: 'API accuracy SLA in §3.2 must update', level: 'high' },
      { doc: 'BC-007', detail: 'Payback model recalculates ROI', level: 'high' },
      { doc: 'ADR-009', detail: 'Model retraining cadence may shift', level: 'medium' },
    ],
  },
  shared: [
    { name: 'Success Metrics', docs: ['PRD-031', 'BC-007', 'FSD-022'] },
    { name: 'Glossary', docs: ['PRD-031', 'FSD-022', 'ADR-009', 'MR-014'] },
    { name: 'Personas', docs: ['PRD-031', 'MR-014'] },
  ],
};

export const generatedContent: Record<string, Block[]> = {
  's-personas': [
    KV([
      ['Priya — Category Planner', 'Owns 1,200 SKUs across 3 regions. Wants to stop firefighting stock-outs and trust a recommendation she can accept in seconds.'],
      ['Marcus — Supply Chain Manager', 'Accountable for working capital. Needs visibility into why the model orders what it does before he signs off.'],
      ['Dana — Store Operations Lead', 'Lives with the consequences of bad forecasts. Wants fewer empty shelves and less backroom overstock.'],
    ]),
    P('Each persona shares one need: a transparent, adjustable recommendation rather than a black-box number.'),
  ],
  's-req-nonfunc': [
    UL([
      'NFR-1 — Generate forecasts for 1,800 stores within a 30-minute nightly batch window.',
      'NFR-2 — 99.9% availability for the recommendation read API during business hours.',
      'NFR-3 — P95 recommendation read latency under 300ms.',
      'NFR-4 — Encrypt all sales data at rest and in transit; SOC 2 Type II compliant.',
      'NFR-5 — Full model lineage and reproducibility for every published forecast.',
    ]),
  ],
  's-scope': [
    KV([
      ['Multi-echelon optimization', 'v1 forecasts demand only; DC-to-store allocation is a fast-follow.'],
      ['Price elasticity modeling', 'Pricing signals are read-only inputs, not optimized.'],
      ['Non-grocery verticals', 'Apparel and seasonal categories deferred to v2.'],
      ['Automated PO submission', 'Recommendations require human acceptance for launch.'],
    ]),
  ],
};

const FILLER_P = 'Drafted from the project\'s grounded context — Market Research, Business Case, and the filled sections of this document. Review and refine before publishing.';

const stubTypes: Record<string, string[]> = {
  'Market Research': ['Market Overview', 'Demand Drivers', 'Competitive Landscape', 'Findings'],
  'Business Case': ['Problem & Opportunity', 'Proposed Investment', 'ROI Model', 'Risks'],
  'Functional Spec': ['System Overview', 'API Contract', 'Planner UX', 'Data Model', 'Edge Cases'],
  'Architecture Decision': ['Context', 'Decision', 'Latency Budget', 'Consequences'],
};

export function makeStubSections(doc: Document): Section[] {
  const titles = stubTypes[doc.type] ?? ['Overview', 'Details'];
  return titles.map((title, i) => ({
    id: `${doc.id}-s${i}`,
    title,
    level: 1,
    status: (i % 3 === 2 ? 'empty' : 'filled') as 'empty' | 'filled',
    author: doc.owner,
    prompt: i % 3 === 2 ? `Generate the "${title}" section for this ${doc.type.toLowerCase()}, grounded in related project documents.` : null,
    blocks: i % 3 === 2 ? [] : [P(`${title} — ${FILLER_P}`)],
  }));
}
